import { useEffect, useState } from "react";
import { Link, Redirect } from "wouter";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Merchant Rewards page — Phase 9.
 *
 * Operations:
 *   • List rewards (active + archived toggle)
 *   • Create a reward (DISCOUNT_PESO / DISCOUNT_PERCENT / FREE_ITEM /
 *     FREE_VISIT / OTHER) — points-cost OR stamps-cost depending on
 *     loyalty mode (collected in the form; the server enforces the
 *     points-XOR-stamps invariant).
 *   • Archive a reward (DELETE /tenant/rewards/:id; voucherEd rewards
 *     keep their snapshot so issued vouchers still render).
 *
 * Designed for cashier laptops + bring-your-own tablets: chunky tap
 * targets, no fancy modals, single-column form layout.
 */

interface RewardListRow {
  id: string;
  name: string;
  description: string | null;
  metadata: { type: string; [k: string]: unknown };
  pointsCost: number | null;
  stampsCost: number | null;
  voucherTtlDays: number;
  stockLimit: number | null;
  stockRemaining: number | null;
  active: boolean;
  archivedAt: string | null;
}

const REWARD_TYPES = [
  { value: "DISCOUNT_PESO", label: "₱ off" },
  { value: "DISCOUNT_PERCENT", label: "% off" },
  { value: "FREE_ITEM", label: "Free item" },
  { value: "FREE_VISIT", label: "Free visit" },
  { value: "OTHER", label: "Other" },
] as const;

export function MerchantRewardsPage() {
  const [rewards, setRewards] = useState<RewardListRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Create form state.
  const [name, setName] = useState("");
  const [type, setType] = useState<typeof REWARD_TYPES[number]["value"]>(
    "DISCOUNT_PESO",
  );
  const [amount, setAmount] = useState("");
  const [percent, setPercent] = useState("");
  const [itemName, setItemName] = useState("");
  const [pointsCost, setPointsCost] = useState("");
  const [stampsCost, setStampsCost] = useState("");
  const [ttlDays, setTtlDays] = useState("30");
  const [stockLimit, setStockLimit] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRewards(null);
      setLoadErr(null);
      const qs = includeArchived ? "?includeArchived=true" : "";
      // GET /tenant/rewards returns the array directly under `data`
      // (envelope shape is `{data: RewardResponse[]}`), not nested
      // under a `rewards` key.
      const r = await merchantFetch<RewardListRow[]>(
        `/api/v1/tenant/rewards${qs}`,
      );
      if (cancelled) return;
      if (!r.ok) {
        setLoadErr(r.message);
        return;
      }
      setRewards(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [includeArchived, refreshKey]);

  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitErr(null);
    const metadata: Record<string, unknown> = { type };
    if (type === "DISCOUNT_PESO") {
      metadata.amount = Number(amount);
    } else if (type === "DISCOUNT_PERCENT") {
      metadata.percent = Number(percent);
    } else if (type === "FREE_ITEM") {
      metadata.itemName = itemName.trim();
    }
    const body: Record<string, unknown> = {
      name: name.trim(),
      metadata,
      voucherTtlDays: Number(ttlDays) || 30,
    };
    const pts = pointsCost.trim() === "" ? null : Number(pointsCost);
    const stmps = stampsCost.trim() === "" ? null : Number(stampsCost);
    if (pts !== null && stmps !== null) {
      setSubmitErr("Set either points or stamps cost — not both.");
      return;
    }
    if (pts === null && stmps === null) {
      setSubmitErr("Set a points or stamps cost.");
      return;
    }
    if (pts !== null) body.pointsCost = pts;
    if (stmps !== null) body.stampsCost = stmps;
    if (stockLimit.trim() !== "") body.stockLimit = Number(stockLimit);

    setSubmitting(true);
    const r = await merchantFetch<{ id: string }>("/api/v1/tenant/rewards", {
      method: "POST",
      body: JSON.stringify(body),
    });
    setSubmitting(false);
    if (!r.ok) {
      setSubmitErr(r.message);
      return;
    }
    // Reset + reload.
    setName("");
    setAmount("");
    setPercent("");
    setItemName("");
    setPointsCost("");
    setStampsCost("");
    setStockLimit("");
    setRefreshKey((k) => k + 1);
  }

  async function onArchive(id: string) {
    if (!window.confirm("Archive this reward? Issued vouchers stay valid.")) {
      return;
    }
    const r = await merchantFetch(`/api/v1/tenant/rewards/${id}`, {
      method: "DELETE",
    });
    if (!r.ok) {
      window.alert(r.message);
      return;
    }
    setRefreshKey((k) => k + 1);
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-2xl px-6 pt-8">
        <MerchantNav current="rewards" />

        <h1 className="mt-6 text-2xl font-bold text-navy">Rewards catalog</h1>
        <p className="mt-1 text-sm text-ink-dark">
          Customers spend points or stamps on these rewards. Issued vouchers
          carry a snapshot of the reward, so archiving doesn't break them.
        </p>

        <section className="mt-6 card">
          <h2 className="font-semibold text-navy">Add a reward</h2>
          <form onSubmit={onSubmit} className="mt-3 grid gap-3">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                Name
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={1}
                maxLength={120}
                className="mt-1 input"
                placeholder="₱50 off, Free latte…"
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                Type
              </span>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as typeof type)}
                className="mt-1 input"
              >
                {REWARD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            {type === "DISCOUNT_PESO" && (
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                  Discount amount (₱)
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 input"
                />
              </label>
            )}
            {type === "DISCOUNT_PERCENT" && (
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                  Discount percent (1–100)
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  required
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  className="mt-1 input"
                />
              </label>
            )}
            {type === "FREE_ITEM" && (
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                  Item name
                </span>
                <input
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  required
                  className="mt-1 input"
                  placeholder="Latte, Carwash basic, etc."
                />
              </label>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                  Points cost
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={0.01}
                  value={pointsCost}
                  onChange={(e) => setPointsCost(e.target.value)}
                  placeholder="e.g. 50"
                  className="mt-1 input"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                  Stamps cost
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={stampsCost}
                  onChange={(e) => setStampsCost(e.target.value)}
                  placeholder="e.g. 10"
                  className="mt-1 input"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                  Voucher TTL (days)
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={3650}
                  value={ttlDays}
                  onChange={(e) => setTtlDays(e.target.value)}
                  className="mt-1 input"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                  Stock limit (optional)
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={stockLimit}
                  onChange={(e) => setStockLimit(e.target.value)}
                  placeholder="Leave blank for unlimited"
                  className="mt-1 input"
                />
              </label>
            </div>

            {submitErr && (
              <p className="text-sm text-red-600">{submitErr}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary mt-1"
            >
              {submitting ? "Saving…" : "Add reward"}
            </button>
          </form>
        </section>

        <section className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-navy">Existing rewards</h2>
            <label className="text-xs text-ink-dark flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
              Show archived
            </label>
          </div>

          {loadErr ? (
            <p className="mt-3 text-sm text-red-600">Couldn't load: {loadErr}</p>
          ) : rewards === null ? (
            <p className="mt-3 text-sm text-ink-dark">Loading…</p>
          ) : rewards.length === 0 ? (
            <p className="mt-3 text-sm text-ink-dark">No rewards yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {rewards.map((r) => (
                <li key={r.id} className="card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-navy">
                        {r.name}{" "}
                        {r.archivedAt && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-ink-dark">
                            archived
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-ink-dark">
                        {r.metadata.type}
                        {" · "}
                        {r.pointsCost !== null
                          ? `${r.pointsCost.toFixed(2)} pts`
                          : `${r.stampsCost} stamps`}
                        {" · TTL "}
                        {r.voucherTtlDays}d
                        {r.stockLimit !== null && (
                          <>
                            {" · "}
                            {r.stockRemaining}/{r.stockLimit} left
                          </>
                        )}
                      </p>
                    </div>
                    {!r.archivedAt && (
                      <button
                        type="button"
                        onClick={() => void onArchive(r.id)}
                        className="text-sm text-red-600 hover:underline shrink-0"
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
